import { useState } from 'react'
import { Wall2D } from '../../../models/Construction'
import { Group, Rect } from 'react-konva'
import { BaseComponentProps } from './EditorWrapper'
import { FLOOR_PLAN_COLORS } from './Elements'

interface WallComponentProps extends BaseComponentProps<Wall2D> {}

export const WallComponent: React.FC<WallComponentProps> = ({
  element,
  scale,
  thicknessMultiplier = 1,
  isSelected = false,
  isHovered = false,
  isHidden = false,
}) => {
  
  const centerX = element.center_point[0] * scale
  const centerY = element.center_point[1] * scale
  const length = element.length * scale
  const thickness = element.thickness * scale * thicknessMultiplier
  const angle = element.angle || 0
  const radians = (angle * Math.PI) / 180

  const [isWallHovered, setIsWallHovered] = useState(isHovered)

  const createWallSegments = () => {
    const allOpenings: Array<{
      center: number
      width: number
      start: number
      end: number
      type: 'window' | 'door' | 'header'
      id: string
    }> = []

    if (element.windows) {
      element.windows.forEach((window) => {
        allOpenings.push({
          center: window.center_location_on_wall,
          width: window.width,
          start: window.center_location_on_wall - window.width / 2,
          end: window.center_location_on_wall + window.width / 2,
          type: 'window',
          id: window.id,
        })
      })
    }

    if (element.doors) {
      element.doors.forEach((door) => {
        allOpenings.push({
          center: door.center_location_on_wall,
          width: door.width,
          start: door.center_location_on_wall - door.width / 2,
          end: door.center_location_on_wall + door.width / 2,
          type: 'door',
          id: door.id,
        })
      })
    }

    if (allOpenings.length === 0) {
      return (
        <Rect
          x={centerX}
          y={centerY}
          visible={!isHidden}
          width={length}
          height={thickness}
          offsetX={length / 2}
          offsetY={thickness / 2}
          fill={
            isSelected
              ? FLOOR_PLAN_COLORS.SELECTED
              : isWallHovered
                ? FLOOR_PLAN_COLORS.HOVER
                : FLOOR_PLAN_COLORS.WALLS
          }
          stroke={isSelected ? '#FF0000' : '#000000'}
          strokeWidth={isSelected ? 2 : 1}
          rotation={angle}
          onMouseEnter={() => setIsWallHovered(true)}
          onMouseLeave={() => setIsWallHovered(false)}
        />
      )
    }

    const sortedOpenings = allOpenings.sort((a, b) => a.center - b.center)

    const mergedOpenings = []
    for (const opening of sortedOpenings) {
      if (mergedOpenings.length === 0) {
        mergedOpenings.push(opening)
      } else {
        const lastOpening = mergedOpenings[mergedOpenings.length - 1]
        if (opening.start <= lastOpening.end) {
          lastOpening.end = Math.max(lastOpening.end, opening.end)
          lastOpening.width = lastOpening.end - lastOpening.start
          lastOpening.center = (lastOpening.start + lastOpening.end) / 2
        } else {
          mergedOpenings.push(opening)
        }
      }
    }

    const segments = []
    let currentPos = 0
    let segmentIndex = 0

    mergedOpenings.forEach((opening) => {
      if (opening.start > currentPos) {
        const segmentLength = (opening.start - currentPos) * scale
        const segmentCenter = (currentPos + opening.start) / 2
        const segmentCenterFromWallStart = segmentCenter - element.length / 2

        const segmentX =
          centerX + segmentCenterFromWallStart * Math.cos(radians) * scale
        const segmentY =
          centerY + segmentCenterFromWallStart * Math.sin(radians) * scale

        segments.push(
          <Rect
            key={`wall-segment-${segmentIndex++}`}
            x={segmentX}
            y={segmentY}
            visible={!isHidden}
            width={segmentLength}
            height={thickness}
            offsetX={segmentLength / 2}
            offsetY={thickness / 2}
            fill={
              isSelected
                ? FLOOR_PLAN_COLORS.SELECTED
                : isWallHovered
                  ? FLOOR_PLAN_COLORS.HOVER
                  : FLOOR_PLAN_COLORS.WALLS
            }
            stroke={isSelected ? '#FF0000' : '#000000'}
            strokeWidth={isSelected ? 2 : 1}
            rotation={angle}
            onMouseEnter={() => setIsWallHovered(true)}
            onMouseLeave={() => setIsWallHovered(false)}
          />,
        )
      }
      currentPos = opening.end
    })

    if (currentPos < element.length) {
      const segmentLength = (element.length - currentPos) * scale
      const segmentCenter = (currentPos + element.length) / 2
      const segmentCenterFromWallStart = segmentCenter - element.length / 2

      const segmentX =
        centerX + segmentCenterFromWallStart * Math.cos(radians) * scale
      const segmentY =
        centerY + segmentCenterFromWallStart * Math.sin(radians) * scale

      segments.push(
        <Rect
          key={`wall-segment-${segmentIndex++}`}
          x={segmentX}
          y={segmentY}
          visible={!isHidden}
          width={segmentLength}
          height={thickness}
          offsetX={segmentLength / 2}
          offsetY={thickness / 2}
          fill={
            isSelected
              ? FLOOR_PLAN_COLORS.SELECTED
              : isWallHovered
                ? FLOOR_PLAN_COLORS.HOVER
                : FLOOR_PLAN_COLORS.WALLS
          }
          stroke={isSelected ? '#FF0000' : '#000000'}
          strokeWidth={isSelected ? 2 : 1}
          rotation={angle}
          onMouseEnter={() => setIsWallHovered(true)}
          onMouseLeave={() => setIsWallHovered(false)}
        />,
      )
    }

    return segments
  }

  return <Group>{createWallSegments()}</Group>
}
